import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  Users, CalendarDays, Clock, Plus, Loader2, Settings, Target, Trophy, RefreshCw,
} from "lucide-react";

const AdminSDR = () => {
  const { API, token } = useAuth();
  const [tab, setTab] = useState("overview");
  const [reps, setReps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [hours, setHours] = useState([]);
  const [calLink, setCalLink] = useState("");
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // Dialogs
  const [createOpen, setCreateOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [repForm, setRepForm] = useState({ name: "", email: "", password: "" });
  const [calInput, setCalInput] = useState("");

  // Filters
  const [filterAgent, setFilterAgent] = useState("");
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");

  const h = { Authorization: `Bearer ${token}` };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [repsR, calR, lbR] = await Promise.all([
        fetch(`${API}/sdr/admin/reps`, { headers: h }),
        fetch(`${API}/sdr/admin/settings/calendar-link`, { headers: h }),
        fetch(`${API}/sdr/leaderboard`, { headers: h }),
      ]);
      if (repsR.ok) setReps(await repsR.json());
      if (calR.ok) { const d = await calR.json(); setCalLink(d.calendar_link || ""); setCalInput(d.calendar_link || ""); }
      if (lbR.ok) setLeaderboard(await lbR.json());
    } catch { // error handled silently
      console.error("Request failed:", error);
    }
    setLoading(false);
  };

  const fetchFiltered = async () => {
    const params = new URLSearchParams();
    if (filterAgent) params.set("agent_code", filterAgent);
    if (filterStart) params.set("start_date", filterStart);
    if (filterEnd) params.set("end_date", filterEnd);
    const qs = params.toString();

    try {
      const [sumR, apptR, hoursR] = await Promise.all([
        fetch(`${API}/sdr/admin/summary?${qs}`, { headers: h }),
        fetch(`${API}/sdr/admin/appointments?${qs}`, { headers: h }),
        fetch(`${API}/sdr/admin/hours?${qs}`, { headers: h }),
      ]);
      if (sumR.ok) setSummary(await sumR.json());
      if (apptR.ok) setAppointments(await apptR.json());
      if (hoursR.ok) setHours(await hoursR.json());
    } catch { // error handled silently
      console.error("Request failed:", error);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchFiltered(); }, [filterAgent, filterStart, filterEnd]);

  const handleCreateRep = async () => {
    if (!repForm.name || !repForm.email || !repForm.password) { toast.error("Fill all fields"); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}/sdr/admin/reps`, {
        method: "POST", headers: { ...h, "Content-Type": "application/json" },
        body: JSON.stringify(repForm),
      });
      const d = await r.json();
      if (r.ok) { toast.success(`SDR created: ${d.agent_code}`); setCreateOpen(false); setRepForm({ name: "", email: "", password: "" }); fetchAll(); }
      else toast.error(d.detail || "Failed");
    } catch { toast.error("Network error"); }
    setCreating(false);
  };

  const handleSaveCalLink = async () => {
    try {
      const r = await fetch(`${API}/sdr/admin/settings/calendar-link`, {
        method: "PUT", headers: { ...h, "Content-Type": "application/json" },
        body: JSON.stringify({ calendar_link: calInput }),
      });
      if (r.ok) { toast.success("Calendar link updated"); setCalLink(calInput); setCalOpen(false); }
      else toast.error("Failed to update");
    } catch { toast.error("Network error"); }
  };

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div></AdminLayout>;

  const totalHours = hours.reduce((s, e) => s + (e.hours || 0), 0);

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="admin-sdr">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#1A2636]">SDR Management</h1>
            <p className="text-sm text-gray-500">Manage reps, appointments, hours & calendar</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={async () => {
                setSyncing(true);
                try {
                  const r = await fetch(`${API}/sdr/admin/sync-calendly`, { method: "POST", headers: h });
                  if (r.ok) { toast.success("Calendly sync complete"); fetchAll(); }
                  else toast.error("Sync failed");
                } catch { toast.error("Sync failed"); }
                setSyncing(false);
              }}
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={syncing}
              data-testid="sync-calendly-btn"
            >
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Sync Calendly
            </Button>
            <Button onClick={() => setCalOpen(true)} variant="outline" size="sm" className="gap-1.5" data-testid="cal-settings-btn"><Settings className="w-3.5 h-3.5" /> Calendar Link</Button>
            <Button onClick={() => setCreateOpen(true)} size="sm" className="bg-sky-600 hover:bg-sky-700 gap-1.5" data-testid="create-sdr-btn"><Plus className="w-3.5 h-3.5" /> New SDR Rep</Button>
          </div>
        </div>

        {/* Calendar link status */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-3 flex items-center gap-3 text-sm">
            <CalendarDays className="w-4 h-4 text-sky-600" />
            <span className="text-gray-500">Calendar embed:</span>
            {calLink ? (
              <span className="text-sky-700 font-mono text-xs truncate max-w-md">{calLink}</span>
            ) : (
              <span className="text-amber-600">Not configured — <button className="underline" onClick={() => setCalOpen(true)}>set it now</button></span>
            )}
          </CardContent>
        </Card>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-gray-100 h-10">
            <TabsTrigger value="overview" className="gap-1.5"><Target className="w-3.5 h-3.5" /> Overview</TabsTrigger>
            <TabsTrigger value="appointments" className="gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> Appointments ({appointments.length})</TabsTrigger>
            <TabsTrigger value="hours" className="gap-1.5"><Clock className="w-3.5 h-3.5" /> Hours</TabsTrigger>
            <TabsTrigger value="reps" className="gap-1.5"><Users className="w-3.5 h-3.5" /> Reps ({reps.length})</TabsTrigger>
            <TabsTrigger value="leaderboard" className="gap-1.5"><Trophy className="w-3.5 h-3.5" /> Leaderboard</TabsTrigger>
          </TabsList>

          {/* Shared Filters - shown on overview, appointments, hours */}
          {["overview", "appointments", "hours"].includes(tab) && (
            <Card className="border-0 shadow-sm mt-4">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="w-48">
                    <Label className="text-xs">SDR Rep</Label>
                    <Select value={filterAgent || "all"} onValueChange={v => setFilterAgent(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-9"><SelectValue placeholder="All Reps" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Reps</SelectItem>
                        {reps.map(r => <SelectItem key={r.agent_code} value={r.agent_code}>{r.name} ({r.agent_code})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Start Date</Label><Input type="date" value={filterStart} onChange={e => setFilterStart(e.target.value)} className="h-9 w-40" /></div>
                  <div><Label className="text-xs">End Date</Label><Input type="date" value={filterEnd} onChange={e => setFilterEnd(e.target.value)} className="h-9 w-40" /></div>
                  <Button variant="outline" size="sm" className="h-9" onClick={() => { setFilterAgent(""); setFilterStart(""); setFilterEnd(""); }}>Clear</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            {summary?.totals && (
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: "Total Appointments", value: summary.totals.total_appointments, icon: CalendarDays, c: "from-sky-500 to-blue-600" },
                  { label: "Total Hours", value: `${summary.totals.total_hours.toFixed(1)}h`, icon: Clock, c: "from-violet-500 to-purple-600" },
                  { label: "Active Reps", value: summary.totals.total_reps, icon: Users, c: "from-amber-500 to-orange-600" },
                ].map((s, i) => (
                  <Card key={s.key || s.name || s.title || i} className="border-0 shadow-sm overflow-hidden">
                    <CardContent className="p-0">
                      <div className="flex items-center gap-3 p-4">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.c} flex items-center justify-center`}>
                          <s.icon className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-gray-400">{s.label}</p>
                          <p className="text-lg font-bold text-[#1A2636]">{s.value}</p>
                        </div>
                      </div>
                      <div className={`h-0.5 bg-gradient-to-r ${s.c}`} />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {summary?.reps?.map(r => (
              <Card key={r.agent_code} className="border-0 shadow-sm">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm text-[#1A2636]">{r.name}</p>
                    <p className="text-xs text-gray-400">{r.email} · <span className="font-mono">{r.agent_code}</span></p>
                  </div>
                  <div className="flex gap-6 text-right text-sm">
                    <div><p className="text-[10px] text-gray-400">Appointments</p><p className="font-bold text-sky-600">{r.appointments}</p></div>
                    <div><p className="text-[10px] text-gray-400">Hours</p><p className="font-bold">{r.hours.toFixed(1)}h</p></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* APPOINTMENTS TAB */}
          <TabsContent value="appointments" className="mt-4">
            {appointments.length ? (
              <div className="space-y-2">
                {appointments.map(a => (
                  <Card key={a.appt_id} className="border-0 shadow-sm">
                    <CardContent className="p-3 flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm text-[#1A2636]">{a.client_name}</span>
                          {a.client_email && <span className="text-xs text-gray-400">{a.client_email}</span>}
                          <Badge variant="outline" className="text-[10px] font-mono">{a.agent_code}</Badge>
                        </div>
                        {a.notes && <p className="text-xs text-gray-500 mt-0.5">{a.notes}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">{new Date(a.date + "T12:00:00").toLocaleDateString()}</p>
                        <p className="text-[10px] text-gray-400">{a.agent_name}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : <div className="text-center py-12 text-gray-400 text-sm"><CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-40" />No appointments found</div>}
          </TabsContent>

          {/* HOURS TAB */}
          <TabsContent value="hours" className="mt-4">
            <div className="flex justify-end mb-3">
              <span className="text-sm text-gray-500">Total: <span className="font-bold text-[#1A2636]">{totalHours.toFixed(1)}h</span></span>
            </div>
            {hours.length ? (
              <div className="space-y-2">
                {hours.map(e => (
                  <div key={e.entry_id} className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[#1A2636]">{e.agent_name || e.agent_code}</span>
                        <Badge variant="outline" className="text-[10px] font-mono">{e.agent_code}</Badge>
                        <span className="text-xs text-gray-400">{new Date(e.date + "T12:00:00").toLocaleDateString()}</span>
                      </div>
                      {e.notes && <p className="text-xs text-gray-500 mt-0.5">{e.notes}</p>}
                    </div>
                    <span className="font-bold text-sky-600">{e.hours}h</span>
                  </div>
                ))}
              </div>
            ) : <div className="text-center py-12 text-gray-400 text-sm"><Clock className="w-8 h-8 mx-auto mb-2 opacity-40" />No hours logged</div>}
          </TabsContent>

          {/* REPS TAB */}
          <TabsContent value="reps" className="space-y-3 mt-4">
            {reps.map(r => (
              <Card key={r.user_id} className="border-0 shadow-sm">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{r.name}</p>
                    <p className="text-xs text-gray-500">{r.email}</p>
                  </div>
                  <span className="font-mono text-sm bg-sky-50 text-sky-700 px-3 py-1 rounded-md">{r.agent_code}</span>
                </CardContent>
              </Card>
            ))}
            {!reps.length && <div className="text-center py-12 text-gray-400 text-sm">No SDR reps yet</div>}
          </TabsContent>

          {/* LEADERBOARD TAB */}
          <TabsContent value="leaderboard" className="mt-4">
            <Card className="border-0 shadow-sm max-w-2xl">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center"><CalendarDays className="w-4 h-4 text-white" /></div>
                  <h3 className="font-bold text-[#1A2636]">Appointments Set</h3>
                </div>
                {leaderboard.length ? leaderboard.map((e, i) => (
                  <div key={e.agent_code} className={`flex items-center justify-between p-3 rounded-lg mb-1 ${i < 3 ? "bg-sky-50/50" : "bg-gray-50"}`}>
                    <div className="flex items-center gap-3">
                      <span className="w-6 text-center font-bold text-sm">{i + 1}</span>
                      <div>
                        <span className="text-sm font-medium">{e.name}</span>
                        <p className="text-[10px] text-gray-400 font-mono">{e.agent_code}</p>
                      </div>
                    </div>
                    <span className="font-bold text-sm">{e.appointments} appts</span>
                  </div>
                )) : <p className="text-sm text-gray-400 text-center py-6">No data</p>}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create SDR Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>New SDR Rep</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Full Name *</Label><Input value={repForm.name} onChange={e => setRepForm({ ...repForm, name: e.target.value })} data-testid="sdr-name" /></div>
            <div><Label className="text-xs">Email *</Label><Input type="email" value={repForm.email} onChange={e => setRepForm({ ...repForm, email: e.target.value })} data-testid="sdr-email" /></div>
            <div><Label className="text-xs">Password *</Label><Input type="password" value={repForm.password} onChange={e => setRepForm({ ...repForm, password: e.target.value })} data-testid="sdr-password" /></div>
            <p className="text-xs text-gray-400">A unique SDR code will be auto-generated.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateRep} disabled={creating} className="bg-sky-600 hover:bg-sky-700" data-testid="sdr-submit">{creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Rep"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Calendar Link Dialog */}
      <Dialog open={calOpen} onOpenChange={setCalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Calendar Embed Link</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-500">Paste the embed URL for your booking calendar (e.g. Calendly). This will be shown to all SDR reps.</p>
            <div><Label className="text-xs">Calendar URL</Label><Input value={calInput} onChange={e => setCalInput(e.target.value)} placeholder="https://calendly.com/your-team/meeting" data-testid="cal-link-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCalLink} className="bg-sky-600 hover:bg-sky-700" data-testid="cal-link-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminSDR;
